import _ from 'lodash';
import moment from 'moment';
import { forwardRef, useImperativeHandle } from 'react';
import { handleAddEcgBookmarks } from '@/entities/bookmark';
import { handleRequestFileUpload, clearApolloCache } from '@/shared/api';
import {
  handleUpdateBeatsAndEvents,
  addIsSavingToChangedData,
  defaultBeatsOptions,
} from '@/entities/beat';
import { handleUpdateHolterHrDailySummary } from '@/entities/heart-rate';
import { handleUpdateHolterProfile } from '@/entities/study';
import { ANNOTATION_TYPE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  AIRP_TAB,
  ID_BOOKMARK_TAB,
  KEY_RECOIL,
  TYPE_BEAT_ENUM,
  TYPE_EVENT_OTHER,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import {
  resetHistoryState,
  updateStatusAndMetadata,
  isValidHttpUrl,
  putFileToUrl,
} from '@/shared/lib';
import { buildGroupEventsPayload } from '@/shared/lib/aiUtils';
import emitter from '@/shared/lib/eventEmitter';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  defaultEventOptions,
  selectActiveTab,
  selectBeatChanges,
  selectEventChanges,
  selectEventNews,
  selectEventOthers,
  selectGroupEventChanges,
  useReportStore,
} from '@/entities/report';

import { logError } from '@/shared/lib/apiHelpers';

export type SummaryFunctionProps = {
  studyId?: any;
  profileId?: any;
  isNotEdit?: boolean;
};

export type SummaryFunctionRef = {
  onClickSaveReport: () => Promise<void>;
  isChangedData: () => boolean;
  resetData: () => void;
};

/**
 * Imperative save controller for the HR Summary tab, exposing save, dirty-check and reset to its parent through a forwarded ref. Binds the TAB_6 slice of the report store, uploads captured strips, posts beat, event, daily-summary, bookmark and holter-profile updates, records the request id in localStorage for AI-processing tracking, then resets modals and undo history.
 *
 * @param props - Study and profile identifiers plus the read-only flag for the tab.
 * @param ref - Forwarded handle receiving onClickSaveReport, isChangedData and resetData.
 * @returns Null — the component renders nothing and works entirely through the ref.
 */
const SummaryFunction = forwardRef<SummaryFunctionRef, SummaryFunctionProps>(
  ({ studyId, profileId, isNotEdit }, ref) => {
    const keyRecoil = KEY_RECOIL.HR_SUMMARY;
    /**
     * Reads the other-type events of the HR Summary tab. Applies the other-events selector for this tab key to a one-shot report-store snapshot.
     *
     * @returns The list of other-type events.
     */
    const getEventOthers = () => selectEventOthers(keyRecoil)(useReportStore.getState());
    const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
    /**
     * Writes the other-type events of the HR Summary tab. Forwards the value or updater to the store action along with this tab's key.
     *
     * @param v - Next other-event list or an updater producing it.
     * @returns Nothing.
     */
    const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
      setEventOthersAction(keyRecoil, v);
    /**
     * Reads the newly created events of the HR Summary tab. Applies the new-events selector for this tab key to a one-shot report-store snapshot.
     *
     * @returns The list of newly created events.
     */
    const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
    const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
    /**
     * Writes the newly created events of the HR Summary tab. Forwards the value or updater to the store action along with this tab's key.
     *
     * @param v - Next new-event list or an updater producing it.
     * @returns Nothing.
     */
    const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
      setEventNewsAction(keyRecoil, v);
    /**
     * Reads the pending edits to existing events of the HR Summary tab. Applies the event-changes selector for this tab key to a one-shot report-store snapshot.
     *
     * @returns The list of edited events.
     */
    const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
    const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
    /**
     * Writes the pending edits to existing events of the HR Summary tab. Forwards the value or updater to the store action along with this tab's key.
     *
     * @param v - Next event-change list or an updater producing it.
     * @returns Nothing.
     */
    const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
      setEventChangesAction(keyRecoil, v);
    /**
     * Reads the pending beat edits of the HR Summary tab. Applies the beat-changes selector for this tab key to a one-shot report-store snapshot instead of subscribing.
     *
     * @returns The list of pending beat changes.
     */
    const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
    const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
    /**
     * Writes the pending beat edits of the HR Summary tab. Forwards the value or updater to the store action along with this tab's key.
     *
     * @param v - Next beat-change list or an updater producing it.
     * @returns Nothing.
     */
    const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
      setBeatChangesAction(keyRecoil, v);
    /**
     * Reads the pending group-event edits of the HR Summary tab. Applies the group-event-changes selector for this tab key to a one-shot report-store snapshot.
     *
     * @returns The list of group event changes.
     */
    const getGroupEventChanges = () =>
      selectGroupEventChanges(keyRecoil)(useReportStore.getState());
    const setGroupEventChangesAction = useReportStore((s) => s.actions.setGroupEventChanges);
    /**
     * Writes the pending group-event edits of the HR Summary tab. Forwards the value or updater to the store action along with this tab's key.
     *
     * @param v - Next group-event change list or an updater producing it.
     * @returns Nothing.
     */
    const setGroupEventChanges = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
      setGroupEventChangesAction(keyRecoil, v);
    /**
     * Reads the daily HR summaries the technician has edited but not yet saved. Pulls updatedDailySummaries off a one-shot report-store snapshot.
     *
     * @returns The list of edited daily summaries.
     */
    const getUpdatedDailySummaries = () => useReportStore.getState().updatedDailySummaries;
    const setUpdatedDailySummaries = useReportStore((s) => s.actions.setUpdatedDailySummaries);
    const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
    /**
     * Closes the modal control state of the HR Summary tab. Delegates to the report-store reset action bound to this tab's key.
     *
     * @returns Nothing.
     */
    const resetControlModal = () => resetControlModalAction(keyRecoil);
    const resetControlModalHrSummary = useReportStore((s) => s.actions.resetControlHrSummaryModal);
    /**
     * Reads the ECG bookmarks currently held in the report store. Pulls the field straight off a one-shot snapshot, so the caller does not re-render when bookmarks change.
     *
     * @returns The current ECG bookmark list.
     */
    const getEcgBookmarks = () => useReportStore.getState().ecgBookmarks;
    const setEcgBookmarks = useReportStore((s) => s.actions.setEcgBookmarks);
    const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
    /**
     * Writes the event options snapshot of the HR Summary tab used by undo and redo. Forwards the value or updater to the store action along with this tab's key.
     *
     * @param v - Next event options value or an updater producing it.
     * @returns Nothing.
     */
    const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
      setEventOptionsAction(keyRecoil, v);
    /**
     * Reads the currently active sub-tab of the Rhythm Events tab from the report store. Applies the TAB_2 active-tab selector to a one-shot snapshot instead of subscribing to it.
     *
     * @returns The active tab identifier of the Rhythm Events tab.
     */
    const getActiveButtonTabRhythmEvent = () =>
      selectActiveTab(KEY_RECOIL.RHYTHM_EVENT)(useReportStore.getState());
    const setPageIndexTabRhythmEventAction = useReportStore((s) => s.actions.setPageIndex);
    /**
     * Writes the page index of the Rhythm Events tab from inside HR Summary. Forwards the value to the store action under the TAB_2 key rather than this tab's own key.
     *
     * @param v - Next page index value or an updater producing it.
     * @returns Nothing.
     */
    const setPageIndexTabRhythmEvent = (
      v: Parameters<typeof setPageIndexTabRhythmEventAction>[1],
    ) => setPageIndexTabRhythmEventAction(KEY_RECOIL.RHYTHM_EVENT, v);
    const setIsDisabledBtn = useReportStore((s) => s.actions.setIsDisabledBtn);
    /**
     * Reads the report metadata — study, profile and edit permission — from the report store. Pulls reportInfo off a one-shot snapshot rather than subscribing.
     *
     * @returns The current report info record, empty before it loads.
     */
    const getReportInfo = () => useReportStore.getState().reportInfo;
    const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
    /**
     * Writes the beat options snapshot of the HR Summary tab used by undo and redo. Forwards the value or updater to the store action along with this tab's key.
     *
     * @param v - Next beat options value or an updater producing it.
     * @returns Nothing.
     */
    const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
      setBeatOptionsAction(keyRecoil, v);
    const setIsPushQueue = useReportStore((s) => s.actions.setIsPushQueue);

    const setOriginalDailySummaries = useReportStore((s) => s.actions.setOriginalDailySummaries);
    /**
     * Builds the beat mutation payload from the pending beat edits. Maps each beat to time, type — with deleted and Q beats remapped to Del and Others — and an isNew flag, then marks both the beat changes and the beat options snapshot as saving.
     *
     * @param beats - Pending beat edits to convert into mutation input.
     * @returns A promise resolving to the beats input object, or undefined when the list is empty.
     */
    const getInputUpdateBeats = async (beats: any[]) => {
      if (beats.length === 0) {
        return undefined;
      }
      const input = {
        beats: _.map(beats, (k) => ({
          time: k?.id,
          type:
            k?.type === TYPE_BEAT_ENUM.DELETED
              ? 'Del'
              : k?.type === TYPE_BEAT_ENUM.Q
                ? 'Others'
                : k?.type,
          isNew: k.oldType === undefined || k.oldType === k.type,
        })),
      };
      setBeatChanges(addIsSavingToChangedData(beats));
      setBeatOptions({
        beatChangesState: addIsSavingToChangedData(getBeatChanges()),
      });
      return input;
    };

    //* Khi capture thì sẽ assign svg vào event rồi lúc nhấn save mới check và upload hình lấy url s3 về gửi lên
    /**
     * Prepares edited HR-summary events for saving by turning freshly drawn strip SVGs into uploaded S3 URLs. Clones changed, new and filtered other events, collects linked event ids, requests one upload URL per pending SVG, PUTs them in parallel, rewrites each capture's stripImgs and its sample-mark start and stop, then builds the group-event payload; errors are logged and swallowed.
     *
     * @param eChanges - Events edited in place whose changes are being saved.
     * @param eNews - Newly created events awaiting their first save.
     * @param eOthers - Other-type events, filtered down to those updated, removed or re-captured.
     * @param groupEventChanges - Pending group-event edits converted into the update payload.
     * @returns A promise resolving to an object carrying changedEvents, allLinkEventIds and updateGroupEvents, or undefined when the upload throws.
     */
    const formatEventsAndUploadCapturedImage = async (
      eChanges: any[] = [],
      eNews: any[] = [],
      eOthers: any[] = [],
      groupEventChanges: any[] = [],
    ) => {
      try {
        const eventOthers = _.filter(
          eOthers,
          (x) => !!x.isUpdatedOther || !!x.isRemovedStrip || !!x.isUpdateImage,
        );
        const changedEvents = _.cloneDeep([...eChanges, ...eNews, ...eventOthers]);
        const allLinkEventIds: any[] = [];
        const tempEventStripUpdate: any[] = [];
        const tempArrImgUpload: any[] = [];
        changedEvents.forEach((x: any, index: number) => {
          if (x.linkEvents?.length) {
            x.linkEvents.forEach((linkEvent: any) => allLinkEventIds.push(linkEvent.idEvent));
          }
          //* if not valid url => is svg => have updated strip
          _.forEach(x.captures, (capture) => {
            if (capture.stripImgs?.length && !isValidHttpUrl(capture.stripImgs[0])) {
              tempEventStripUpdate.push({
                index,
                captureId: capture.captureId,
                imagesCount: capture.stripImgs.length,
              });
              tempArrImgUpload.push(...capture.stripImgs);
            }
          });
        });
        if (changedEvents.length && tempArrImgUpload.length) {
          const uploadFileUrls = await handleRequestFileUpload('svg', tempArrImgUpload.length);
          if (uploadFileUrls?.length) {
            const putFileToUrlPromises = _.map(uploadFileUrls, (uploadFileUrl, index) =>
              putFileToUrl(uploadFileUrl, tempArrImgUpload[index], false),
            );
            await Promise.all(putFileToUrlPromises);
            _.forEach(tempEventStripUpdate, (data) => {
              const foundUpdatedStripEvent = changedEvents[data.index];
              const findCapture = _.find(
                foundUpdatedStripEvent.captures,
                (obj) => obj.captureId === data.captureId,
              );
              const findDraw = _.find(
                findCapture,
                (obj) => obj.type === ANNOTATION_TYPE.SAMPLE_MARK,
              );
              if (findDraw) {
                findCapture.start = findDraw.startTime;
                findCapture.stop = findDraw.endTime;
              }
              for (let i = 0; i < data.imagesCount; i += 1) {
                if (!_.isArray(findCapture.stripImgs)) {
                  findCapture.stripImgs = [uploadFileUrls.shift()];
                } else {
                  findCapture.stripImgs[i] = uploadFileUrls.shift();
                }
              }
            });
          }
        }

        const updateGroupEvents = buildGroupEventsPayload(groupEventChanges);
        return { changedEvents, allLinkEventIds, updateGroupEvents };
      } catch (error) {
        console.log('Failed to upload file:', error);
      }
    };

    /**
     * Builds the event mutation payload from the changed events and group-event edits. Maps other-type events to a strips array keyed by the sample-mark annotation, maps rhythm events to id, start, stop and type — routing deleted and artifact types to isDeleted or isMarkedAsArtifact unless the event is linked — rebuilds strips for re-captured events, then flags news, changes and others as saving.
     *
     * @param changedEvents - Events whose edits are turned into mutation input.
     * @param allLinkEventIds - Ids of linked events, used to keep a deleted linked event on its old type.
     * @param updateGroupEvents - Group-event payload merged into the input when non-empty.
     * @returns A promise resolving to the input carrying updateEvents and updateGroupEvents, or undefined when nothing changed.
     */
    const getInputUpdateEvents = async ({
      changedEvents = [] as any[],
      allLinkEventIds = [] as any[],
      updateGroupEvents,
    }: {
      changedEvents?: any[];
      allLinkEventIds?: any[];
      updateGroupEvents?: any;
    }) => {
      const hasGroupEventChanges = !_.isEmpty(updateGroupEvents);
      const hasChangedEvents = changedEvents.length > 0;
      if (!hasChangedEvents && !hasGroupEventChanges) {
        return undefined;
      }
      const updateEvents = _.map(changedEvents, (k: any) => {
        if (k.type === TYPE_EVENT_OTHER) {
          const strip = k.captures?.[0];
          const isUpdatedOldEvent = k.isUpdatedOther && k.idEvent;
          const sampleMarkDraw = _.find(k.draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
          return {
            start: k.start,
            stop: k.stop,
            type: k.type,
            title: k.title,
            isIncludedToReport: isUpdatedOldEvent ? k.isIncludedToReport : !_.isEmpty(k.stripImgs),
            id: k.idEvent || undefined,
            isDeleted: k.isRemovedStrip || undefined,
            strips: strip?.originalId
              ? [
                  {
                    comment: k.technicianComments ?? undefined,
                    action: k.isRemovedStrip ? 'delete' : k.action || 'update',
                    id: strip?.originalId || undefined,
                    channel: strip?.isUpdateOtherImage
                      ? (sampleMarkDraw.channelIndex || 0) + 1
                      : undefined,
                    draw: strip?.isUpdateOtherImage ? k.draw : undefined,
                    images: strip?.isUpdateOtherImage ? k.stripImgs : undefined,
                  },
                ]
              : [
                  {
                    start: k.stripStart ?? k.start,
                    stop: k.stripStop ?? k.stop,
                    images: k.stripImgs,
                    isSelected: !_.isEmpty(k.stripImgs),
                    comment: k.technicianComments ?? undefined,
                    draw: k.draw,
                    action: k.isRemovedStrip ? 'delete' : k.action || 'update',
                    channel: (sampleMarkDraw?.channelIndex || 0) + 1,
                    id: strip?.id || undefined,
                  },
                ],
          };
        }
        const formatUpdateEvent: any = {
          id: k.idEvent,
          start: k.start ? moment(k.start).toISOString() : undefined,
          stop: k.stop ? moment(k.stop).toISOString() : undefined,
          isReviewed: k.isReviewed,
          strips: k.isUpdateImage ? [] : undefined,
        };
        if ([TYPE_BEAT_ENUM.DELETED, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT].includes(k.type)) {
          if (allLinkEventIds.includes(k.idEvent) && k.type === TYPE_BEAT_ENUM.DELETED) {
            formatUpdateEvent.type = k.oldType;
          } else {
            formatUpdateEvent[
              k.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ? 'isMarkedAsArtifact' : 'isDeleted'
            ] = true;
          }
        } else {
          formatUpdateEvent.type = k.type;
        }
        if (k.isUpdateImage) {
          _.forEach(
            _.filter(
              [...k.captures, ...(k.removedCaptures || [])],
              (x) => !!x.action || k.isRemovedStrip,
            ),
            (capture) => {
              const findDraw = _.find(
                capture.draw,
                (obj) => obj.type === ANNOTATION_TYPE.SAMPLE_MARK,
              );
              const { originalStrip } = capture || {};
              if (k.isRemovedStrip && capture.action === 'delete') {
                formatUpdateEvent.strips.push({
                  action: 'delete',
                  id: capture.originalId,
                });
              } else if (!_.isEmpty(originalStrip)) {
                formatUpdateEvent.strips.push({
                  id: capture.originalId,
                  action: capture.action,
                  comment:
                    originalStrip.comment === capture.technicianComments
                      ? undefined
                      : capture.technicianComments,
                  start: _.isEqual(capture.stripImgs, originalStrip.images)
                    ? undefined
                    : findDraw.startTime,
                  stop: _.isEqual(capture.stripImgs, originalStrip.images)
                    ? undefined
                    : findDraw.endTime,
                  images: _.isEqual(capture.stripImgs, originalStrip.images)
                    ? undefined
                    : capture.stripImgs,
                  draw: _.isEqual(capture.stripImgs, originalStrip.images)
                    ? undefined
                    : capture.draw,
                  channel: _.isEqual(capture.stripImgs, originalStrip.images)
                    ? undefined
                    : (findDraw?.channelIndex || 0) + 1,
                });
              } else {
                formatUpdateEvent.strips.push({
                  start: findDraw.startTime,
                  stop: findDraw.endTime,
                  images: capture.stripImgs,
                  isSelected: !_.isEmpty(capture.stripImgs),
                  comment: capture.technicianComments ?? undefined,
                  draw: capture.draw,
                  action: k.isRemovedStrip ? 'delete' : capture.action,
                  id: capture.originalId || undefined,
                  channel: (findDraw?.channelIndex || 0) + 1,
                });
                _.assign(formatUpdateEvent, { isIncludedToReport: !_.isEmpty(capture.stripImgs) });
              }
            },
          );
        }
        return formatUpdateEvent;
      });
      const input = {
        updateGroupEvents: !_.isEmpty(updateGroupEvents) ? updateGroupEvents : undefined,
        updateEvents: _.filter(updateEvents, (x) => !x.willBeRemoved),
      };
      // Perform grouping action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventNewsState: addIsSavingToChangedData(getEventNews()),
        eventChangesState: addIsSavingToChangedData(getEventChanges()),
        eventOthersState: addIsSavingToChangedData(getEventOthers()),
      }));
      setEventOthers(addIsSavingToChangedData(getEventOthers()));
      setEventChanges(addIsSavingToChangedData(getEventChanges()));
      setEventNews(addIsSavingToChangedData(getEventNews()));
      return input;
    };

    /**
     * Sends the beat and event edits of the HR Summary tab to the backend as one request. Short-circuits when beats, events, links and group changes are all empty, builds both payloads, then posts them with the study, profile, tab and request id; a thrown error is logged and reported as failure.
     *
     * @param info - Carries beatChanges, changedEvents, allLinkEventIds, updateGroupEvents and requestId.
     * @returns A promise resolving to true when the update request was sent successfully.
     */
    const updateBeatsAndEvents = async (info: any) => {
      const { beatChanges, changedEvents, allLinkEventIds, updateGroupEvents, requestId } =
        info || {};
      if (
        beatChanges.length === 0 &&
        changedEvents.length === 0 &&
        _.isEmpty(allLinkEventIds) &&
        _.isEmpty(updateGroupEvents)
      ) {
        return false;
      }
      const beatData = await getInputUpdateBeats(beatChanges);
      const eventData = await getInputUpdateEvents({
        changedEvents,
        allLinkEventIds,
        updateGroupEvents,
      });
      try {
        const input = {
          studyId,
          profileId,
          tab: AIRP_TAB.HR_SUMMARY,
          requestId,
          beatData,
          eventData,
        };
        await handleUpdateBeatsAndEvents(input);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    };

    /**
     * Persists the technician's edits to the daily HR summaries. Reshapes each row into date, avgHr and the min and max HR entries with their beat heads and tails, posts them for the profile, merges the returned summaries into the original list, clears the pending edits and — when no beat or event update is in flight — clears the Apollo cache and re-renders the ECG viewer; errors are logged and toasted.
     *
     * @param updateData - Edited daily summary rows to persist.
     * @param isUpdateBeatsAndEvents - True when a beat and event update was already sent, suppressing the cache clear and viewer refresh.
     * @returns A promise resolving to false, since this path never waits on a socket confirmation.
     */
    const updateDailySummaries = async (updateData: any[], isUpdateBeatsAndEvents: any) => {
      try {
        if (_.isEmpty(updateData)) return false;
        const formatData = _.map(updateData, (item) => ({
          date: item.date,
          avgHr: item.avgHr,
          maxHr: {
            time: item.maxHr?.time,
            userUpdated: item.maxHr?.userUpdated,
            value: item.maxHr?.value,
            hrsOnBeat: {
              head: item.maxHr?.hrsOnBeat?.head,
              tail: item.maxHr?.hrsOnBeat?.tail,
            },
          },
          minHr: {
            time: item.minHr?.time,
            userUpdated: item.minHr?.userUpdated,
            value: item.minHr?.value,
            hrsOnBeat: {
              head: item.minHr?.hrsOnBeat?.head,
              tail: item.minHr?.hrsOnBeat?.tail,
            },
          },
          suggestHrsOnBeat: item.suggestHrsOnBeat,
        }));

        const { mctDailySummaries } = await handleUpdateHolterHrDailySummary({
          profileId,
          mctDailySummaries: formatData,
        });

        if (mctDailySummaries) {
          setOriginalDailySummaries((prev) => {
            if (mctDailySummaries) {
              const cloneDailySummaries = _.cloneDeep(prev);
              _.forEach(mctDailySummaries, (dailySummary) => {
                const foundDailySummary = _.find(
                  cloneDailySummaries,
                  (x) => x.date === dailySummary.date,
                );
                if (foundDailySummary) {
                  _.assign(foundDailySummary, dailySummary);
                }
              });
              return cloneDailySummaries;
            }
            return prev;
          });
          setUpdatedDailySummaries([]);
          if (!isUpdateBeatsAndEvents) {
            await clearApolloCache();
            emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
          }
        }
        return false;
      } catch (error) {
        logError('Failed to save: ', error);
        toastrError(error.message, 'Error');
        return false;
      }
    };

    /**
     * Saves the ECG bookmarks the technician added during the session. Removes the bookmarks that still lack an id, posts them for the study and profile, merges the created ones back sorted by center, and resets the Rhythm Events page index when its bookmark sub-tab is showing; errors are logged and toasted.
     *
     * @param ecgBookmarks - Current bookmark list, from which the unsaved entries are taken.
     * @returns A promise resolving to false, since bookmark saves need no socket response.
     */
    const updateEcgBookmarks = async (ecgBookmarks: any[] = []) => {
      try {
        const clonedEcgBookmarks = _.cloneDeep(ecgBookmarks);
        const newEcgBookmarks = _.remove(clonedEcgBookmarks, (x) => !x.id);
        if (_.isEmpty(newEcgBookmarks)) return false;
        const addedEcgBookmarks = await handleAddEcgBookmarks({
          studyId,
          profileId,
          ecgBookmarks: _.map(newEcgBookmarks, (x) => ({
            start: x.start,
            stop: x.stop,
            notes: x.notes,
          })),
        });
        setEcgBookmarks(_.sortBy(clonedEcgBookmarks.concat(addedEcgBookmarks), [(x) => x.center]));
        // activeTabState (Recoil, Task 10) is disjoint from the ID_BOOKMARK_TAB literal - pre-existing gap.
        if ((getActiveButtonTabRhythmEvent() as any) === ID_BOOKMARK_TAB) {
          setPageIndexTabRhythmEvent({ index: 0 });
        }
        //* Ecg bookmark dont need socket response so return false
        return false;
      } catch (error) {
        logError('Failed to save: ', error);
        toastrError(error.message, 'Error');
        return false;
      }
    };

    /**
     * Reports whether the HR Summary tab still holds unsaved work. Gathers beat, event, group-event, other-event, daily-summary and ECG-bookmark changes, optionally dropping entries already flagged isSaving when the check comes from a save click, and is true when any bucket is non-empty.
     *
     * @param isClickSave - When true, entries already being saved are excluded from the check.
     * @returns True when at least one unsaved change remains.
     */
    const checkChangedData = (isClickSave = false) => {
      const beatChanges = getBeatChanges();
      const eventChanges = getEventChanges();
      const eventNews = getEventNews();
      const groupEventChanges = getGroupEventChanges();
      const updatedDailySummaries = getUpdatedDailySummaries();
      // eventOthersState (Recoil, Task 10) is typed IEventOther[], missing the isSaving field this
      // component reads locally - pre-existing gap.
      const eventOthers: any[] = getEventOthers();
      const ecgBookmarks = getEcgBookmarks();
      const hasChangedEcgBookmarks = ecgBookmarks.find((x) => !x.id);
      const filterNotSavingNews = isClickSave ? _.filter(eventNews, (x) => !x.isSaving) : eventNews;
      const filterNotSavingChanges = isClickSave
        ? _.filter(eventChanges, (x) => !x.isSaving)
        : eventChanges;
      const filterNotSavingOthers = isClickSave
        ? _.filter(eventOthers, (x) => !x.isSaving)
        : eventOthers;
      const filterNotSavingBeats = isClickSave
        ? _.filter(beatChanges, (x) => !x.isSaving)
        : beatChanges;
      return (
        !!filterNotSavingBeats.length ||
        !!filterNotSavingChanges.length ||
        !!filterNotSavingNews.length ||
        !!groupEventChanges.length ||
        !!filterNotSavingOthers.length ||
        !!updatedDailySummaries.length ||
        !!hasChangedEcgBookmarks
      );
    };

    useImperativeHandle(ref, () => ({
      onClickSaveReport: async () => {
        if (!checkChangedData(true) && !isNotEdit) {
          return;
        }
        setIsPushQueue(true);
        emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: true, isSaving: true });
        const requestId = `requests:${profileId}:${Date.now()}`;

        const { changedEvents, allLinkEventIds, updateGroupEvents } =
          await formatEventsAndUploadCapturedImage(
            getEventChanges(),
            getEventNews(),
            getEventOthers(),
            getGroupEventChanges(),
          );
        // reportInfoState (Recoil) is typed {} | IReportInfo - pre-existing gap, needs a cast to destructure.
        const info: any = getReportInfo();
        const lastModified = moment();
        const keyStorage = `requestId-${info.profileId}-${info.studyFid}`;
        let requestData: any = localStorage.getItem(keyStorage);
        if (_.isEmpty(requestData) || !Array.isArray(requestData)) {
          requestData = [
            {
              requestId,
              type: 'HR_SUMMARY',
            },
          ];
        } else {
          requestData.push({
            requestId,
            type: 'HR_SUMMARY',
          });
        }
        localStorage.setItem(keyStorage, JSON.stringify(requestData));
        // localStorage.setItem's value param is typed string, but this passes a boolean - pre-existing.
        localStorage.setItem(`ai_process-${info.profileId}`, false as any);
        localStorage.setItem(`ai_saving-${info.profileId}`, true as any);
        const updateHolterProfileInput = {
          id: info.profileId,
          lastModified,
        };

        const isUpdateBeatsAndEvents = await updateBeatsAndEvents({
          beatChanges: getBeatChanges(),
          changedEvents,
          allLinkEventIds,
          updateGroupEvents,
          requestId,
        });

        const promise = [
          updateEcgBookmarks(getEcgBookmarks()),
          updateDailySummaries(getUpdatedDailySummaries(), isUpdateBeatsAndEvents),
          handleUpdateHolterProfile(updateHolterProfileInput),
          // updateStatusAndMetadata's StudyMetadataValue (src/Store/dbMetaData.ts, out of scope) types
          // lastModified as number, but this passes a Moment - pre-existing.
          updateStatusAndMetadata({ lastModified } as any),
        ];

        // update last modified check download data
        const [isSuccessEcgBookmark, isSuccessDailySummaries] = await Promise.all(promise);
        // *: If all update data fail
        if (!(isSuccessEcgBookmark || isSuccessDailySummaries || isUpdateBeatsAndEvents)) {
          emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false });
          setUpdatedDailySummaries([]);
        } else {
          resetControlModal();
          resetControlModalHrSummary();
        }
        setEventOptions(defaultEventOptions);
        resetHistoryState();
        setTimeout(() => {
          setIsDisabledBtn(false);
        }, 1000);
      },
      isChangedData: () => checkChangedData() && !isNotEdit,
      resetData: () => {
        setBeatChanges([]);
        setEventChanges([]);
        setEventNews([]);
        setGroupEventChanges([]);
        setEventOthers([]);
        setBeatOptions(defaultBeatsOptions);
        setUpdatedDailySummaries([]);
        setEcgBookmarks((prev) => prev.filter((x) => x.id));
        setEventOptions(defaultEventOptions);
        //* Redraw heatmap chart when reset review
        setTimeout(() => {
          emitter.emit(EMITTER_CONSTANTS.UPDATE_HEATMAP_CHART_DATA);
        }, 0);
      },
    }));

    return null;
  },
);

export default SummaryFunction;
